import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Edit from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { Button, Box, Grid, Typography, Alert, Slide } from "@mui/material";

import { dataExplorerActions, dataEditActions } from "../../store";
import {
  BasinData,
  DatasetsData,
  PeopleData,
  ProjectsData,
  SitesData,
} from "./DatasetViewTabs";

const ALERT_AUTO_HIDE_DURATION = 2000;
/**
 * Renders the view for a dataset, with back button and tabs for metadata, downloads, and contact
 *
 * @param {boolean} useMobileLayout - Whether to use the mobile layout.
 * @returns {JSX.Element} The dataset view component.
 */
const DatasetView = ({ useMobileLayout }) => {
  const dispatch = useDispatch();
  const selectedRow = useSelector((state) => state.dataExplorer.selectedRow);
  const dataChanged = useSelector((state) => state.dataEdit.hasDataChanged);
  const showAlert = useSelector((state) => state.dataEdit.isAlertVisible);
  const selectedEntity = useSelector(
    (state) => state.dataExplorer.selectedEntity
  );

  // set timer for alert
  useEffect(() => {
    if (showAlert) {
      setTimeout(() => {
        dispatch(dataEditActions.showAlert(false));
      }, ALERT_AUTO_HIDE_DURATION);
    }
  }, [showAlert, dispatch]);

  const handleBack = () => {
    dispatch(dataExplorerActions.setSelectedRow(null));
    dispatch(dataEditActions.showAlert(false));
  };

  const fieldClicked = selectedRow.field;
  const title = selectedRow.row[fieldClicked];

  const headerStyle = {
    variant: "h4",
    sx: {
      color: "primary.contrastText",
    },
  };

  const subheaderStyle = {
    variant: "h5",
    sx: {
      color: "primary.contrastText",
      mt: 3,
      mb: 1,
    },
  };

  const renderComponent = () => {
    switch (selectedEntity) {
      case "basins":
        return (
          <BasinData
            subheaderStyle={subheaderStyle}
            useMobileLayout={useMobileLayout}
          />
        );
      case "datasets":
        return (
          <DatasetsData
            subheaderStyle={subheaderStyle}
            useMobileLayout={useMobileLayout}
          />
        );
      case "people":
        return (
          <PeopleData
            subheaderStyle={subheaderStyle}
            useMobileLayout={useMobileLayout}
          />
        );
      case "projects":
        return (
          <ProjectsData
            subheaderStyle={subheaderStyle}
            useMobileLayout={useMobileLayout}
          />
        );
      case "sites":
        return (
          <SitesData
            subheaderStyle={subheaderStyle}
            useMobileLayout={useMobileLayout}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      id="dataset-view"
      sx={{
        display: "flex",
        flexDirection: "column",
        // width: "100%",
        maxWidth: "lg",
        backgroundColor: "primary.light",
        pb: 3,
        px: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/*Alert updating user of changes made*/}
        <Slide
          in={showAlert}
          timeout={500}
          sx={{
            maxWidth: "sm",
            width: "100%",
            justifyContent: "center",
          }}
        >
          {dataChanged ? (
            <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
              Dataset successfully updated.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ backgroundColor: "#e0e0e0" }}>
              No changes made.
            </Alert>
          )}
        </Slide>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            px: 0,
            py: 2,
            color: "primary.contrastText",
          }}
        >
          Back
        </Button>
        <Button
          startIcon={<Edit />}
          onClick={() => dispatch(dataEditActions.showDatasetForm(true))}
          sx={{
            px: 0,
            py: 2,
            color: "primary.contrastText",
          }}
        >
          Edit
        </Button>
      </Box>
      <Typography {...headerStyle}>{title}</Typography>
      <Grid container>{renderComponent()}</Grid>
    </Box>
  );
};
export default DatasetView;
